// Physical sight geometry shared by AOE placement and its server validator.
// PHP twin: includes/functions/janus/encounter/ft3d_sight_core.php.
import { segmentHit3d, boxIsRoofed } from './ft3d-shape-core.js';
const EPS = 1e-6;
const cross = (a, b, p) => (b.x-a.x)*(p.y-a.y)-(b.y-a.y)*(p.x-a.x);

// Matches the authoring compiler's room-clipped stairwell hull and 0.05 ft inset.
export function stairSightContour(connection, box) {
  const points = [], axes = connection.scaleXYZ ?? [1, 1, 1];
  const q = connection.rotationQuarterTurns ?? 0, anchor = connection.anchorFt;
  if (!Number.isInteger(q) || q < 0 || q > 3 || axes.length !== 3 || !anchor) return [];
  for (const family of ['support', 'floorCutout']) {
    for (const polygon of connection.asset?.geometry?.footprints?.[family] ?? []) {
      for (const p of polygon.pointsM ?? []) {
        const x = p[0]*axes[0]/.3048, y = p[2]*axes[2]/.3048;
        const [rx, ry] = [[x,y],[y,-x],[-x,-y],[-y,x]][q];
        points.push({ x: rx+anchor.x, y: ry+anchor.y });
      }
    }
  }
  points.sort((a,b) => a.x-b.x || a.y-b.y);
  const half = list => {
    const out = [];
    for (const p of list) {
      while (out.length >= 2 && cross(out.at(-2), out.at(-1), p) <= EPS) out.pop();
      out.push(p);
    }
    out.pop();
    return out;
  };
  let contour = [...half(points), ...half([...points].reverse())];
  for (const [axis, edge, sign] of [['x',box.x.min,1],['x',box.x.max,-1],['y',box.y.min,1],['y',box.y.max,-1]]) {
    const input = contour; contour = [];
    if (input.length < 3) return [];
    let a = input.at(-1);
    for (const b of input) {
      const ain = (a[axis]-edge)*sign >= -EPS, bin = (b[axis]-edge)*sign >= -EPS;
      if (ain !== bin) {
        const t = (edge-a[axis])/(b[axis]-a[axis]);
        contour.push({ x:a.x+t*(b.x-a.x), y:a.y+t*(b.y-a.y) });
      }
      if (bin) contour.push(b);
      a = b;
    }
  }
  if (contour.length < 3) return [];
  const cx = contour.reduce((n,p)=>n+p.x,0)/contour.length;
  const cy = contour.reduce((n,p)=>n+p.y,0)/contour.length;
  return contour.map(p => {
    const dx=cx-p.x, dy=cy-p.y, d=Math.hypot(dx,dy), amount=Math.min(.05,d*.1);
    return d > EPS ? { x:p.x+dx/d*amount, y:p.y+dy/d*amount } : p;
  });
}

function stairApertureAt(shape, box, x, y) {
  return (shape.authoring?.verticalConnections ?? []).some(connection => {
    if (connection.targetRoomId !== box.id) return false;
    const contour = stairSightContour(connection, box);
    return contour.length >= 3 && contour.every((a,i) => cross(a,contour[(i+1)%contour.length],{x,y}) > EPS);
  });
}

export function sightSegmentHit3d(shape, source, target, { containToUnion = false } = {}) {
  let best = segmentHit3d(shape, source, target, { mode: 'sight', containToUnion });
  const dz = target.z-source.z;
  if (Math.abs(dz) < EPS) return best;
  for (const box of shape.boxes) {
    if (box.navigationOpenGround) continue;
    const surfaces = [box.floor];
    if (boxIsRoofed(box)) surfaces.push(box.creation?.physicalCeilingFt ?? box.ceiling);
    for (const [index,z] of surfaces.entries()) {
      const t = (z-source.z)/dz;
      // Ground contact from above is legal; aiming up through the underside is not.
      if (t <= EPS || t > 1+EPS || (t >= 1-EPS && dz < 0) || (best && t >= best.t)) continue;
      const x = source.x+t*(target.x-source.x), y = source.y+t*(target.y-source.y);
      if (x < box.x.min || x > box.x.max || y < box.y.min || y > box.y.max) continue;
      if (shape.boxes.some(other => other !== box && !other.navigationOpenGround
        && x > other.x.min+EPS && x < other.x.max-EPS && y > other.y.min+EPS && y < other.y.max-EPS
        && z > other.floor+EPS && z < other.ceiling-EPS)) continue;
      // An upper floor owns the shared surface, including its authored stair opening.
      if (index > 0 && shape.boxes.some(upper => upper.id !== box.id && Math.abs(upper.floor-z)<.01
        && x>=upper.x.min && x<=upper.x.max && y>=upper.y.min && y<=upper.y.max)) continue;
      if (index === 0 && stairApertureAt(shape,box,x,y)) continue;
      best = { t,x,y,z,axis:'z',at:z };
    }
  }
  return best;
}
